/**
 * Bulk actions component
 * Actions for selected results
 */

import { Button, Space, Typography, Popconfirm } from 'antd';
import {
  DeleteOutlined,
  CheckSquareOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { formatNumber } from '@/utils/formatters';

const { Text } = Typography;

interface BulkActionsProps {
  selectedCount: number;
  totalCount?: number;
  onDelete?: () => void;
  onSelectAll?: () => void;
  onClearSelection?: () => void;
  loading?: boolean;
}

export function BulkActions({
  selectedCount,
  totalCount,
  onDelete,
  onSelectAll,
  onClearSelection,
  loading,
}: BulkActionsProps) {
  if (selectedCount === 0) {
    return null;
  }

  return (
    <Space size={8}>
      <CheckSquareOutlined style={{ color: 'var(--color-primary)' }} />
      <Text strong>
        {formatNumber(selectedCount)} selected
      </Text>

      {totalCount && selectedCount < totalCount && onSelectAll && (
        <Button type="link" size="small" onClick={onSelectAll}>
          Select all {formatNumber(totalCount)}
        </Button>
      )}

      {onDelete && (
        <Popconfirm
          title="Delete selected places?"
          description={`This will permanently delete ${selectedCount} place(s).`}
          onConfirm={onDelete}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button
            icon={<DeleteOutlined />}
            danger
            disabled={loading}
            loading={loading}
          >
            Delete
          </Button>
        </Popconfirm>
      )}

      {onClearSelection && (
        <Button
          icon={<CloseOutlined />}
          onClick={onClearSelection}
          disabled={loading}
          size="small"
          type="text"
        >
          Clear
        </Button>
      )}
    </Space>
  );
}

/**
 * Simple selection info
 */
export function SelectionInfo({
  selectedCount,
  totalCount,
  onClear,
}: {
  selectedCount: number;
  totalCount: number;
  onClear?: () => void;
}) {
  if (selectedCount === 0) return null;

  return (
    <Space>
      <Text type="secondary">
        {formatNumber(selectedCount)} of {formatNumber(totalCount)} selected
      </Text>
      {onClear && (
        <Button type="link" size="small" onClick={onClear}>
          Clear
        </Button>
      )}
    </Space>
  );
}

export default BulkActions;
